/**
 * @fullvsl/app-shell/views/preview — player preview view (FUL-225).
 *
 * Mounts the real FullVslPlayer with the project's config (source URL, aspect
 * ratio, CTA, overlays, resume) plus the OverlayEditor for live overlay
 * editing. Shows the event stream and an embed code snippet.
 *
 * The player is destroyed and recreated on each mount so config changes from
 * the editor view are fully applied.
 */
import type { PlayerEvent } from "../../../contracts/src/contracts";
import type { VslStore } from "../store";
import { FullVslPlayer } from "../../../player/src/fullvsl-player";
import type { AspectRatioInput } from "../../../player/src/fullvsl-player";
import { YouTubePlayer } from "../../../player/src/youtube-player";
import { OverlayEditor } from "../../../player/src/fullvsl-overlay-editor";
import { el, append } from "../dom";

export function renderPreviewView(
  store: VslStore,
  projectId: string,
  _navigate: (path: string) => void,
): { mount: HTMLElement; cleanup: () => void } {
  const project = store.get(projectId);
  const wrapper = el("div");

  if (!project) {
    const empty = el("div", "fva-empty");
    append(empty, el("h3", null, "VSL não encontrada"));
    append(wrapper, empty);
    return { mount: wrapper, cleanup: () => {} };
  }

  const layout = el("div", "fva-preview-layout");

  // ---- Left: player mount + event log ----
  const playerCol = el("div", "fva-preview-player");
  const playerMount = el("div", "fva-preview-panel");
  playerMount.style.padding = "16px";
  const playerHost = el("div");
  playerHost.style.width = "100%";
  playerHost.style.maxWidth = "640px";
  playerHost.style.margin = "0 auto";
  playerMount.appendChild(playerHost);

  // Controls bar under the player
  const ctrlBar = el("div");
  ctrlBar.style.display = "flex";
  ctrlBar.style.gap = "10px";
  ctrlBar.style.flexWrap = "wrap";
  ctrlBar.style.alignItems = "center";
  ctrlBar.style.marginTop = "10px";

  // Safe-zone toggle
  const safeToggle = document.createElement("label");
  safeToggle.className = "fva-check";
  const safeCb = document.createElement("input");
  safeCb.type = "checkbox";
  safeToggle.appendChild(safeCb);
  safeToggle.appendChild(document.createTextNode(" Exibir áreas seguras"));
  ctrlBar.appendChild(safeToggle);

  // Aspect ratio quick-switch
  const ratioLabel = el("span", "fva-vsl-meta", "Proporção:");
  ratioLabel.style.marginLeft = "8px";
  ctrlBar.appendChild(ratioLabel);
  const ratioSelect = document.createElement("select");
  ratioSelect.className = "fva-select";
  ratioSelect.style.width = "auto";
  for (const r of ["auto", "16:9", "9:16", "1:1", "4:5", "3:4", "custom"] as const) {
    const opt = document.createElement("option");
    opt.value = r;
    opt.textContent = r;
    if (project.aspectRatio === r) opt.selected = true;
    ratioSelect.appendChild(opt);
  }
  ratioSelect.style.padding = "4px 8px";
  ctrlBar.appendChild(ratioSelect);

  // Event log panel
  const eventPanel = el("div", "fva-preview-panel");
  append(eventPanel, el("p", "fva-preview-panel-title", "Fluxo de eventos"));
  const eventLog = el("div", "fva-event-log");

  append(playerCol, playerMount, ctrlBar, eventPanel);
  eventPanel.appendChild(eventLog);
  append(layout, playerCol);

  // ---- Right: overlay editor + embed snippet ----
  const sideCol = el("div", "fva-preview-side");

  // Editor de overlays panel (dark to match editor theme)
  const overlayPanel = el("div", "fva-preview-panel");
  overlayPanel.style.padding = "0";
  const overlayPanelHeader = el("div");
  overlayPanelHeader.style.padding = "12px 16px";
  overlayPanelHeader.style.borderBottom = "1px solid var(--fv-border-light)";
  append(overlayPanelHeader, el("p", "fva-preview-panel-title", "Editor de overlays"));
  overlayPanel.appendChild(overlayPanelHeader);
  const overlayHost = el("div");
  overlayHost.style.padding = "12px";
  overlayPanel.appendChild(overlayHost);

  // Embed snippet panel
  const embedPanel = el("div", "fva-preview-panel");
  append(embedPanel, el("p", "fva-preview-panel-title", "Código de incorporação"));
  const embedOrigin = typeof location !== "undefined" ? location.origin : "https://fullvsl.example.com";
  const embedSnippet = `<!-- Incorporação FullVSL para ${project.name} -->
<script src="${embedOrigin}/embed/fullvsl.js" async></script>
<div data-fullvsl="${project.id}"></div>`;
  const snippetPre = el("div", "fva-snippet", embedSnippet);
  const copyBtn = el("button", "fva-snippet-copy", "Copiar");
  copyBtn.addEventListener("click", () => {
    try {
      navigator.clipboard?.writeText(embedSnippet);
      copyBtn.textContent = "Copiado ✓";
      setTimeout(() => (copyBtn.textContent = "Copiar"), 2000);
    } catch {
      copyBtn.textContent = "Falha ao copiar";
    }
  });
  append(embedPanel, copyBtn, snippetPre);

  append(sideCol, overlayPanel, embedPanel);
  append(layout, sideCol);

  append(wrapper, layout);

  // ---- Mount the player ----
  // Event log helper (shared by both player modes).
  const eventRows: HTMLElement[] = [];
  function logEvent(ev: PlayerEvent): void {
    const row = el("div", "fva-event-row");
    const p = ev as unknown as Record<string, unknown>;
    const detail = [
      typeof p.milestone === "number" ? `${p.milestone}%` : "",
      typeof p.pct === "number" ? `${Math.round(p.pct * 10) / 10}%` : "",
      typeof p.positionSec === "number" ? `${p.positionSec.toFixed(1)}s` : "",
    ].filter(Boolean).join(" · ");
    append(row, el("span", "fva-event-name", ev.name), el("span", "fva-event-meta", detail));
    eventLog.prepend(row);
    eventRows.push(row);
    if (eventRows.length > 30) {
      const old = eventRows.shift();
      old?.remove();
    }
  }

  // FUL-243: use YouTubePlayer for YouTube sources, FullVslPlayer for HTML5.
  const isYoutube = project.sourceType === "youtube";
  const playerOpts = {
    sourceUrl: project.sourceUrl,
    container: playerHost,
    aspectRatio: (project.aspectRatio === "custom" ? "custom" : project.aspectRatio) as AspectRatioInput,
    customRatio: project.customRatio,
    muted: project.autoplay.muted,
    showSafeZones: false,
    vslId: project.id,
    autoplay: project.autoplay,
    progressBar: project.progressBar,
    resume: project.resume,
    brandColor: project.brandColor,
    emitAnalyticsToParent: project.emitAnalyticsToParent,
    cta: project.cta,
    overlays: project.overlays,
    ctaHeadline: project.ctaHeadline,
    ctaSubtext: project.ctaSubtext,
    ctaButtonLabel: project.ctaButtonLabel,
    onEvent: (ev: PlayerEvent) => logEvent(ev),
  };

  const player = isYoutube
    ? new YouTubePlayer(playerOpts)
    : new FullVslPlayer(playerOpts);

  // Wire controls
  safeCb.addEventListener("change", () => {
    player.setShowSafeZones(safeCb.checked);
  });
  ratioSelect.addEventListener("change", () => {
    const val = ratioSelect.value as AspectRatioInput;
    player.setAspectRatio(val, val === "custom" ? project.customRatio ?? { w: 21, h: 9 } : undefined);
  });

  // Mount overlay editor — side-effecting mount; the editor calls onChange
  // which wires overlays back to the player + store.
  void new OverlayEditor({
    container: overlayHost,
    overlays: project.overlays,
    onChange: (overlays) => {
      player.setOverlays(overlays);
      store.setOverlays(project.id, overlays);
    },
  });

  return {
    mount: wrapper,
    cleanup: () => {
      player.destroy();
    },
  };
}
